import { useRef, useState, type ChangeEvent } from 'react';
import type { PhotoIdentification } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { fileToDataUrl } from '../hooks/image.js';
import { BarcodeGate } from './BarcodeGate.js';
import { useToast } from './ui.js';

interface Props {
  /** Codigo ja conhecido: vai junto para a IA nao inventar outro. */
  barcode?: string | null;
  /** Usuario ja confirmou que o produto nao tem codigo. */
  noBarcode?: boolean;
  /** Codigo informado no passo anterior a foto. */
  onBarcode: (barcode: string | null) => void;
  onResult: (result: PhotoIdentification, photo: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Le a embalagem por foto (IA de visao) de dentro de qualquer formulario de
 * produto. Serve tanto para produto novo quanto para completar um cadastro que
 * ja tem codigo de barras — ter o codigo nao impede mandar a foto.
 *
 * O codigo vem primeiro: sem ele, o botao abre o passo de escanear/digitar
 * antes da foto. E o codigo que reconhece o produto na proxima compra, e tê-lo
 * em maos ainda evita gastar uma leitura de IA em produto ja catalogado.
 */
export function PhotoAiButton({
  barcode,
  noBarcode,
  onBarcode,
  onResult,
  label,
  className,
  disabled,
}: Props) {
  const { settings } = useAppData();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [pedindoCodigo, setPedindoCodigo] = useState(false);

  const disponivel = settings.photoAiAvailable && settings.photoAiEnabled;
  const temCodigo = Boolean(barcode?.trim()) || Boolean(noBarcode);

  function escolherFoto() {
    if (!temCodigo) {
      setPedindoCodigo(true);
      return;
    }
    fileRef.current?.click();
  }

  /** Resolvido o codigo, a foto vem na sequencia, sem um toque a mais. */
  function aoResolverCodigo(codigo: string | null) {
    setPedindoCodigo(false);
    onBarcode(codigo);
    // O clique precisa acontecer depois do render que fecha o passo do codigo.
    setTimeout(() => fileRef.current?.click(), 0);
  }

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      if (!dataUrl) {
        toast('Nao consegui abrir essa imagem.', 'error');
        return;
      }
      // O decodificador so entra em cena quando e preciso: importado sob
      // demanda, ele fica fora do pacote inicial do app.
      const lido = barcode?.trim()
        ? barcode.trim()
        : await import('../hooks/useScanner.js').then((m) => m.decodeStill(dataUrl));
      const result = await api.identifyPhoto(dataUrl, lido);
      if (!result.suggestion) {
        toast(result.message ?? 'Nao deu para identificar o produto nesta foto.', 'error');
      }
      onResult(result, dataUrl);
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!disponivel) return null;

  return (
    <>
      {pedindoCodigo ? (
        <BarcodeGate
          title="Primeiro, o codigo de barras"
          hint="Escaneie ou digite o codigo; em seguida abro a camera para ler a embalagem. E o codigo que reconhece o produto na proxima compra."
          onResolve={aoResolverCodigo}
          onCancel={() => setPedindoCodigo(false)}
        />
      ) : null}

      <button
        type="button"
        className={className ?? 'btn btn-block'}
        disabled={busy || disabled || pedindoCodigo}
        onClick={escolherFoto}
      >
        {busy ? 'Lendo a embalagem…' : (label ?? '📷 Ler a embalagem por foto (IA)')}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(event) => void handleFile(event)}
      />
    </>
  );
}
